import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Menu, X } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { GodModeToggle } from '@/components/GodModeToggle';
import { TournamentHubSidebar } from './TournamentHubSidebar';

// Story 082/083 — tournament hub layout. AppHeader at the top
// (added in 083 so the hub matches every other director page),
// then a per-tournament header bar (tournament name + mobile
// hamburger), then sidebar + main slot. Desktop renders the
// sidebar inline; mobile hides it and exposes the drawer via the
// hamburger. Drawer is a hand-rolled fixed-positioned panel —
// Sheet wasn't in the project's shadcn set.
//
// The back-to-overview link is a mobile-only shortcut from sub-pages
// (players, rounds, edit, …) back to /tournaments/:id. On desktop
// the sidebar already exposes that nav so the link is hidden; on
// the overview itself it would be a no-op so it's hidden there too.

interface Props {
  tournamentId: string;
  tournamentName: string;
  godMode: boolean;
  setGodMode: (on: boolean) => void;
  role: 'owner' | 'co-director';
  /** Story 097 — game id; chess-only sidebar items hide for other games. */
  game?: string;
  children: ReactNode;
}

export function TournamentHubLayout({
  tournamentId,
  tournamentName,
  godMode,
  setGodMode,
  role,
  game,
  children,
}: Props) {
  const { t } = useTranslation('director');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change — sidebar items navigate away from
  // the hub, but if for some reason the user lands back on this page
  // we don't want a stale-open drawer.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // The overview index is /tournaments/:id with no further segment.
  // Strip a trailing slash so the literal-equality check is robust to
  // the browser sometimes leaving one in place.
  const isOnOverview =
    location.pathname.replace(/\/$/, '') === `/tournaments/${tournamentId}`;

  return (
    <div
      data-testid="tournament-hub-page"
      className="flex min-h-dvh flex-col bg-background"
    >
      <AppHeader />
      <header className="flex items-center gap-2 border-b bg-card px-3 py-2">
        {!isOnOverview && (
          <Link
            data-testid="tournament-hub-back-to-overview"
            to={`/tournaments/${tournamentId}`}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm font-medium hover:bg-accent/30 md:hidden"
          >
            <ChevronLeft className="h-4 w-4" />
            {t('tournament_hub.back_to_overview')}
          </Link>
        )}
        <h1 className="truncate text-base font-semibold md:text-lg">
          {tournamentName}
        </h1>
        <div className="ml-auto flex items-center gap-2">
          {/* Story 083 — one God Mode toggle governs every god-mode
              action across the hub sub-pages. Story 093 — owner-only. */}
          {role === 'owner' && (
            <GodModeToggle godMode={godMode} setGodMode={setGodMode} />
          )}
          <button
            type="button"
            aria-label={t('tournament_hub.open_menu')}
            data-testid="tournament-hub-mobile-toggle"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex items-center justify-center rounded p-2 md:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      {godMode && (
        <div className="border-b border-red-300 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-950">
          <p
            data-testid="god-mode-banner"
            className="mx-auto max-w-6xl text-sm font-medium text-red-700 dark:text-red-300"
          >
            {t('tournament_hub.god_mode_banner')}
          </p>
        </div>
      )}

      <div className="flex flex-1">
        <aside
          data-testid="tournament-hub-sidebar"
          className="hidden w-60 shrink-0 border-r bg-card p-3 md:flex md:flex-col"
        >
          <TournamentHubSidebar tournamentId={tournamentId} role={role} game={game} />
        </aside>

        <main className="flex-1 p-4 md:p-6">{children}</main>
      </div>

      {drawerOpen && (
        <>
          <div
            data-testid="tournament-hub-mobile-backdrop"
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            data-testid="tournament-hub-mobile-drawer"
            className="fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r bg-card p-3 shadow-xl md:hidden"
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium">{tournamentName}</span>
              <button
                type="button"
                aria-label={t('tournament_hub.close_menu')}
                data-testid="tournament-hub-mobile-close"
                onClick={() => setDrawerOpen(false)}
                className="inline-flex items-center justify-center rounded p-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <TournamentHubSidebar
              tournamentId={tournamentId}
              role={role}
              game={game}
              onNavigate={() => setDrawerOpen(false)}
            />
          </aside>
        </>
      )}
    </div>
  );
}
